import { APITypes } from '../../../primary-record-types';
import React from 'react';
import { getIntl } from '../../../utils/intl';
import getDateString from '../../../utils/getDateString';
import { composeValidators, minValue, numeric, required } from '../../../utils/validators';
import { usePromptButton } from '../PromptButton';
import { ContentStack } from '../../v2BaseComponents/Stack';
import { Checkbox, Input } from '../../v2BaseComponents/Inputs';
import { PromptActions } from '../../v2BaseComponents/Prompt';

const intl = getIntl('useSendInvoicePrompt', {
    sendInvoice: 'Client Invoice - {date}',
    header: 'Send Client Invoice',
    description: 'Description',
    daysUntilDue: 'Days Until Due',
    send: 'Send',
    cancel: 'Cancel',
    dueUponReceipt: 'Due Upon Receipt',
});

export type UseSendInvoicePromptArgs = {
    onSend: (request: APITypes.organizationService.CreateOrganizationClientRecordInvoiceRequest) => void;
};

export type UseSendInvoicePromptReturnValue = {
    Prompt: React.ReactNode;
    onPromptSendInvoice: () => void;
};

const getDefaultDescription = () => intl('sendInvoice', { date: getDateString(new Date().toISOString(), 'date') });

const defaultDaysUntilDue = '7';

export const useSendInvoicePrompt = ({ onSend }: UseSendInvoicePromptArgs): UseSendInvoicePromptReturnValue => {
    const [description, setDescription] = React.useState(getDefaultDescription());
    const [daysUntilDue, setDaysUntilDue] = React.useState(defaultDaysUntilDue);
    const [dueUponReceipt, setDueUponReceipt] = React.useState(false);
    const daysUntilDueError = dueUponReceipt
        ? undefined
        : composeValidators(required, numeric, minValue(0))(daysUntilDue);

    const promptButton = usePromptButton({
        promptTitle: intl('header'),
        renderPromptContents: (onClose) => (
            <ContentStack>
                <Input
                    title={intl('description')}
                    value={description}
                    onChange={(e) => {
                        setDescription(e.nativeEvent.text);
                    }}
                    testID="description-input"
                />
                <Checkbox
                    title={intl('dueUponReceipt')}
                    value={dueUponReceipt}
                    onChange={(value) => {
                        setDueUponReceipt(value);
                        setDaysUntilDue(value ? '0' : defaultDaysUntilDue);
                    }}
                    testID="dueUponReceipt-checkbox"
                />
                {dueUponReceipt ? null : (
                    <Input
                        title={intl('daysUntilDue')}
                        value={daysUntilDue}
                        onChange={(e) => {
                            setDaysUntilDue(e.nativeEvent.text);
                        }}
                        testID="daysUntilDue-input"
                        error={daysUntilDueError}
                        isRequired
                    />
                )}
                <PromptActions
                    actions={[
                        {
                            title: intl('send'),
                            isDisabled: !!daysUntilDueError,
                            onPress: () => {
                                onSend({
                                    description,
                                    daysUntilDue: parseInt(daysUntilDue),
                                });
                                onClose();
                            },
                            testID: 'send-invoice',
                            icon: 'Send',
                        },
                        {
                            title: intl('cancel'),
                            onPress: () => {
                                onClose();
                            },
                            testID: 'cancel-send-invoice',
                            icon: 'XCircle',
                            variant: 'secondary',
                        },
                    ]}
                />
            </ContentStack>
        ),
    });

    return {
        Prompt: promptButton.Prompt,
        onPromptSendInvoice: promptButton.onPress,
    };
};
